import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Checkbox, FormControlLabel } from '@mui/material';

import NumberFormat from 'react-number-format';

import { RootState } from 'src/store/reducers';

import { IAddressDetails } from '@interfaces/myProfileInterfaces';

import EditAddressForm from './EditAddressForm';

import CheckBlack from '@images/svg-icons/checkBlack.svg';
import CheckBlackEmpty from '@images/svg-icons/checkBlackEmpty.svg';
import Modal from '@components/styled-UI/Modal';
import { deleteAddress, setDefaultAddress } from '@services/myProfileServices';

interface Props {
    shipping: IAddressDetails;
    billing?: IAddressDetails;
}

const AddressDetails = ({ shipping, billing }: Props) => {
    const config = useSelector((state: RootState) => state.common.config);
    const isMobile = useSelector((state: RootState) => state.common.isMobile);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);

    const dispatch = useDispatch();

    const setAsDefaultAddressHandler = () => {
        dispatch(
            setDefaultAddress(
                config.mockRequest,
                config.storeId,
                shipping.nickName,
                billing?.nickName
            )
        );
    };

    const editAddressHandler = () => {
        setIsEditFormOpen(true);
    };

    const removeAddressHandler = () => {
        dispatch(
            deleteAddress(config.mockRequest, config.storeId, shipping?.nickName, billing?.nickName)
        );
        setIsDeleteModalOpen(false);
    };

    const renderActionButtons = () => {
        return (
            <div className="action-buttons" role="region">
                <button
                    className="remove-address primary-button-negative black button-size-small"
                    onClick={() => setIsDeleteModalOpen(true)}
                    aria-label="remove address"
                >
                    Remove
                </button>
                <button
                    className="edit-address primary-button black button-size-small"
                    onClick={editAddressHandler}
                    aria-label="edit address"
                >
                    Edit
                </button>
            </div>
        );
    };

    return (
        <>
            {!isEditFormOpen && (
                <div className="content-container address-details">
                    <div className="address-details_header">
                        <FormControlLabel
                            data-analytics_available_call="0"
                            classes={{
                                label: 'default-address_label primary-message medium bold',
                                root: `default-address ${
                                    shipping?.primary === 'true' ? 'default' : ''
                                }`,
                            }}
                            control={
                                <Checkbox
                                    size="small"
                                    icon={<CheckBlackEmpty />}
                                    checkedIcon={<CheckBlack />}
                                    checked={shipping?.primary === 'true'}
                                    onChange={setAsDefaultAddressHandler}
                                />
                            }
                            label={
                                shipping?.primary === 'true'
                                    ? 'My default address'
                                    : 'Set as default address'
                            }
                        />
                        {!isMobile && renderActionButtons()}
                        <span className="street">{shipping?.addressLine[0]}</span>
                    </div>

                    <hr />

                    <div className="address-details_body">
                        {!!shipping && (
                            <div
                                className="address-data"
                                role="region"
                                aria-label="shipping address details"
                            >
                                <div className="primary-message small align-left uppercase bold">
                                    Shipping address
                                </div>

                                <p aria-label="shipping address name">
                                    {shipping.firstName} {shipping.lastName}
                                </p>
                                <p aria-label="shipping address phone number">
                                    <NumberFormat
                                        type="tel"
                                        format="(+1) ###-###-####"
                                        value={shipping.phone1}
                                        displayType={'text'}
                                    ></NumberFormat>
                                </p>
                                <p aria-label="shipping address">
                                    {shipping.addressLine[0]}, {shipping.city}, {shipping.state},{' '}
                                    {shipping.zipCode}
                                </p>
                            </div>
                        )}

                        <div
                            className="address-data"
                            role="region"
                            aria-label="billing address details"
                        >
                            <div className="primary-message small align-left uppercase bold">
                                Billing address
                            </div>

                            {!!billing ? (
                                <>
                                    <p aria-label="billing address name">
                                        {billing.firstName} {billing.lastName}{' '}
                                    </p>
                                    <p aria-label="billing address">
                                        {billing.addressLine[0]}, {billing.city}, {billing.state},{' '}
                                        {billing.zipCode}
                                    </p>
                                </>
                            ) : (
                                <p aria-label="billing address same as shipping address">
                                    Same as shipping address
                                </p>
                            )}
                        </div>
                    </div>
                    {isMobile && renderActionButtons()}
                </div>
            )}

            {isEditFormOpen && (
                <EditAddressForm
                    isDefault={shipping?.primary === 'true'}
                    shippingNickName={shipping.nickName}
                    billingNickName={billing?.nickName}
                    defaultValues={{
                        address1: shipping.addressLine[0],
                        address2: shipping.addressLine[1],
                        city: shipping.city,
                        firstname: shipping.firstName,
                        lastname: shipping.lastName,
                        phoneNumber: shipping.phone1,
                        state: shipping.state,
                        zipcode: shipping.zipCode,
                        billingAddress1: billing?.addressLine[0] || '',
                        billingAddress2: billing?.addressLine[1] || '',
                        billingCity: billing?.city || '',
                        billingFirstname: billing?.firstName || '',
                        billingLastname: billing?.lastName || '',
                        billingState: billing?.state || '',
                        billingZipcode: billing?.zipCode || '',
                    }}
                />
            )}

            <Modal isOpen={isDeleteModalOpen} close={() => setIsDeleteModalOpen(false)} isCentered>
                <div className="address-details_remove-modal">
                    <div
                        role="region"
                        aria-label="Address details delete modal title"
                        className="title"
                    >
                        Are you sure you want to remove this address?
                    </div>
                    <div className="buttons-container">
                        <button
                            role="region"
                            aria-label="Address details delete modal remove button"
                            data-element-id="AddressOverlay_Remove"
                            data-description="Confirm"
                            className="primary-button button-size-medium"
                            onClick={removeAddressHandler}
                        >
                            Yes, remove
                        </button>
                        <button
                            role="region"
                            aria-label="Address details delete modal cancel button"
                            data-element-id="AddressOverlay_Remove"
                            data-description="Back"
                            className="primary-button-negative button-size-medium"
                            onClick={() => setIsDeleteModalOpen(false)}
                        >
                            No, close
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AddressDetails;
